/**
 * RevenueCat Configuration
 * 
 * UPDATED: Now using newer platform-specific SDK keys (post-2022)
 * These keys are recommended for new apps and provide better platform separation.
 */

export const REVENUECAT_CONFIG = {
  // Platform-specific keys (loaded from environment variables)
  // These are from the RevenueCat dashboard for the Marune project
  platformKeys: {
    ios: process.env.REACT_APP_REVENUECAT_IOS_API_KEY || '',
    android: process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY || '',
    web: process.env.REACT_APP_REVENUECAT_WEB_API_KEY || ''
  },
  
  // Entitlement identifiers - MUST match original app
  entitlements: {
    peak: 'peak',
    adFree: 'ad-free'
  },
  
  // Product identifiers - DEFINITIVE MAPPING from user
  products: {
    ios: {
      monthly: 'iosmonthly', // iOS monthly product
      yearly: 'iosannual'    // iOS annual product
    },
    android: {
      monthly: 'monthly',    // Android monthly product  
      yearly: 'yearly'       // Android annual product
    }
  },
  
  // Feature flags for gradual rollout (loaded from environment variables)
  featureFlags: {
    enablePhoenixIAP: process.env.REACT_APP_ENABLE_REVENUECAT === 'true',
    enableWebPurchases: false, // Web purchases not ready yet
    useRevenueCatAsTruth: true, // Use RevenueCat as source of truth
    enableSubscriptionSync: true,
    forceRestoreOnMigration: false
  },
  
  // Emergency overrides
  emergency: {
    disableRevenueCat: false,
    honorLegacySubscriptions: true,
    firebaseOnlyMode: false
  }
};

/**
 * Get the appropriate SDK key for the current platform
 */
export function getSDKKey(platform?: 'ios' | 'android' | 'web'): string {
  const platformKey = platform && REVENUECAT_CONFIG.platformKeys[platform];

  if (platformKey) {
    return platformKey;
  }

  // Default to iOS key if no platform specified
  const defaultKey = REVENUECAT_CONFIG.platformKeys.ios;

  if (!defaultKey) {
    throw new Error('RevenueCat API keys not configured. Please check your environment variables.');
  }

  return defaultKey;
}

/**
 * Check if IAP is enabled
 */
export function isIAPEnabled(): boolean {
  return REVENUECAT_CONFIG.featureFlags.enablePhoenixIAP && 
         !REVENUECAT_CONFIG.emergency.disableRevenueCat;
}

/**
 * Check if we should use Firebase as fallback (ONLY for web or when RevenueCat is disabled)
 * Note: honorLegacySubscriptions does NOT trigger fallback mode - it's handled separately
 * in the subscription status logic to give access if EITHER RevenueCat OR Firebase shows active.
 */
export function shouldUseFirebaseFallback(): boolean {
  return REVENUECAT_CONFIG.emergency.firebaseOnlyMode;
}

/**
 * Check if we should honor legacy Firebase subscriptions
 * When true, users with isPeakSub: true in Firebase get access even if RevenueCat shows inactive
 */
export function shouldHonorLegacySubscriptions(): boolean {
  return REVENUECAT_CONFIG.emergency.honorLegacySubscriptions;
}